import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Grid,
  Typography,
  Button,
  TextField,
  useTheme,
  useMediaQuery,
  Box,
  Skeleton,
} from "@mui/material";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import { RootState } from "../../store/store";
import { NearbyBranch } from "../../models/warehouse/types";
import { useSelector } from "react-redux";
import expiringLocalStorage from "../../services/expiringLocalStorage";
import MapComponent from "../../components/shared/mapComponent";
import BranchCard from "./branchCard";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import MapTypeEnum from "../../../enums/mapTypeEnum";
import dataService from "../../services/dataService";
import { NearbyBranchesRequest } from "../../models/product/types";

export interface BranchAvailabilityDialogProps {
  open: boolean;
  onClose: () => void;
  productId: string | null;
}

interface PositionResult {
  positionLatitude: number;
  positionLongitude: number;
}

const BranchAvailabilityDialog: React.FC<BranchAvailabilityDialogProps> = ({
  open,
  onClose,
  productId,
}) => {
  const { warehouseName, warehouseCode } = useSelector(
    (state: RootState) => state.warehouseState.selectedWarehouse
  );
  const [sortBy, setSortBy] = useState<number>(3);
  const [searchTmpQuery, setSearchTmpQuery] = useState<string>("");
  const [searchMapQuery, setSearchMapQuery] = useState<string>("");
  const [mapType, setMapType] = useState<MapTypeEnum>(
    MapTypeEnum.CurrentBranch
  );
  const [showValidationMessage, setShowValidationMessage] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [currentWarehouse, setCurrentWarehouse] =
    useState<NearbyBranch | null>();
  const [otherWarehouses, setOtherWarehouses] = useState<NearbyBranch[]>([]);
  const [visibleCount, setVisibleCount] = useState(3);

  const getCurrentPositionAsync = (): Promise<PositionResult | null> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        return resolve(null);
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            positionLatitude: position.coords.latitude,
            positionLongitude: position.coords.longitude,
          });
        },
        () => {
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 5000 }
      );
    });
  };

  const loadBranches = async (sort: number, searchQuery: string) => {
    setIsBranchesLoading(true);
    const coords = await getCurrentPositionAsync();

    const request: NearbyBranchesRequest = {
      clientUtcOffsetInMinutes: new Date().getTimezoneOffset() * -1,
      isCurrentBranch: false,
      productAvailabilityBranchFilter: 0,
      productAvailabilityBranchSortBy: sort,
      positionLatitude: coords?.positionLatitude ?? 0,
      positionLongitude: coords?.positionLongitude ?? 0,
      branchSearchInput: searchQuery,
    };

    try {
      const response = await dataService.GetNearByBranches(
        productId ?? 0,
        request
      );
      if (response.success && response.data) {
        setOtherWarehouses(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch sorted nearby branches", err);
    } finally {
      setIsBranchesLoading(false);
    }
  };

  const initializeBranchData = async () => {
    setIsCurrentLoading(true);
    try {
      const coords = await getCurrentPositionAsync();
      const currentRequest: NearbyBranchesRequest = {
        clientUtcOffsetInMinutes: new Date().getTimezoneOffset() * -1,
        isCurrentBranch: true,
        productAvailabilityBranchFilter: 0,
        productAvailabilityBranchSortBy: sortBy,
        positionLatitude: coords?.positionLatitude ?? 0,
        positionLongitude: coords?.positionLongitude ?? 0,
        branchSearchInput: "",
      };

      const currentRes = await dataService.GetNearByBranches(
        productId ?? 0,
        currentRequest
      );

      if (currentRes.success && currentRes.data?.length > 0) {
        const selected =
          currentRes.data.find((b) => b.isSelected) ?? currentRes.data[0];
        setCurrentWarehouse(selected);
      }
    } catch (err) {
      console.error("Error initializing branch data:", err);
    } finally {
      setIsCurrentLoading(false);
    }

    await loadBranches(sortBy, searchTmpQuery);
  };

  const [isCurrentLoading, setIsCurrentLoading] = useState(false);
  const [isBranchesLoading, setIsBranchesLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    initializeBranchData();
  }, [open, productId, sortBy, searchTmpQuery]);

  useEffect(() => {
    if (!open || !navigator.permissions) return;

    let permissionStatus: PermissionStatus | null = null;

    const handleChange = () => {
      if (permissionStatus?.state === "granted") {
        initializeBranchData();
      }
    };

    navigator.permissions
      .query({ name: "geolocation" as PermissionName })
      .then((status) => {
        permissionStatus = status;
        status.addEventListener("change", handleChange);
      })
      .catch(() => {});

    return () => {
      permissionStatus?.removeEventListener("change", handleChange);
    };
  }, [open]);

  // const filterWarehouses = useCallback(
  //   (warehouses: NearbyBranch[]) => {
  //     const filtered = warehouses.map((x: NearbyBranch) => ({
  //       id: x.branch.warehouseCode,
  //       name: x.branch.warehouseName,
  //       fullAddress: x.branch.warehouseFullAddress,
  //       contactPhoneNumber: x.branch.contactPhoneNumber,
  //       isSelected: x.branch.warehouseCode === warehouseCode,
  //     }));

  //     if (currentWarehouse?.branch?.warehouseCode !== warehouseCode) {
  //       const newWarehouse = warehouses.find(
  //         (x) => x.branch.warehouseCode === warehouseCode
  //       );
  //       if (newWarehouse) setCurrentWarehouse(newWarehouse);
  //     }
  //   },
  //   [warehouseCode, currentWarehouse]
  // );

  const handleDialogClose = () => {
    onClose();
    handleClear();
  };

  const handleMapLocationSelected = async (
    latitude: number | null,
    longitude: number | null
  ) => {
    // if (!isAuthenticated && (latitude === null || longitude === null)) {
    //     setShowValidationMessage(true);
    // } else if (!isAuthenticated && latitude !== null && longitude !== null && open && warehouseCode) {
    //     const res = await dataService.getAllBranchesOrderedByLocation({ latitude, longitude });
    //     filterWarehouses(res.data);
    // } else if (isAuthenticated && open && warehouseCode) {
    //     const res = await dataService.getAllBranches(warehouseCode, searchTmpQuery);
    //     if (res.data) filterWarehouses(res.data);
    // }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setSearchMapQuery(searchTmpQuery);
      setMapType(MapTypeEnum.SearchText);
      loadBranches(sortBy, searchTmpQuery);
    }
  };

  const handleClear = () => {
    setSearchTmpQuery("");
    setSearchMapQuery("");
    loadBranches(sortBy, "");
    setMapType(MapTypeEnum.CurrentBranch);
  };

  const handleMapSearch = () => {
    setSearchMapQuery(searchTmpQuery);
    setMapType(MapTypeEnum.SearchText);
    loadBranches(sortBy, searchTmpQuery);
  };

  const handleUseCurrentLocation = () => {
    setMapType(MapTypeEnum.UseMyCurrentLocation);
  };

  const setSelectedWarehouseCode = async (code: string) => {
    var response = await dataService.setSelectedWarehouse(code);
    if (response.success) {
      expiringLocalStorage.setItem(
        expiringLocalStorage.localStorageKeys.geolocatedWarehouse,
        code
      );
      window.location.reload();
    }
  };

  const handleSortChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedSort = e.target.value;
    setSortBy(Number(selectedSort));
    loadBranches(Number(selectedSort), searchTmpQuery);
  };

  const handleTemplateClick = () => {
    document.getElementById("addToTemplateButton")?.click();
    handleDialogClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth={false}
      fullWidth
      className="warehouseModal"
    >
      <DialogContent>
        <Grid item xs={12} textAlign={"center"}>
          <Button onClick={handleDialogClose} style={{ float: "right" }}>
            <CloseSharpIcon />
          </Button>
        </Grid>
        <div>
          <Typography
            className="productAvailabilityTitle"
            variant="h5"
            sx={{ textAlign: { xs: "left", md: "center" } }}
            gutterBottom
          >
            PRODUCT AVAILABILITY
          </Typography>
          <Typography
            className="productAvailabilitySubTitle"
            sx={{ textAlign: { xs: "left", md: "center" } }}
            variant="subtitle2"
            gutterBottom
          >
            You will see pricing and inventory based on the selected branch
          </Typography>

          <Grid container spacing={4}>
            {/* LEFT SIDE: Current + Nearby Branches */}
            <Grid item xs={12} md={7}>
              {/* Current Branch Header */}
              <Typography className="branchHeader" variant="subtitle1">
                Current Branch
              </Typography>
              {isCurrentLoading && !currentWarehouse ? (
                <Skeleton variant="rounded" height={90} sx={{ mb: 2 }} />
              ) : (
                currentWarehouse && (
                  <BranchCard nearbyBranch={currentWarehouse} />
                )
              )}
              {/* Nearby Branches Header + Sort Dropdown */}
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                sx={{ mt: 4 }}
              >
                <Grid item>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    flexWrap="wrap"
                  >
                    <Typography className="branchHeader" variant="subtitle1">
                      {searchTmpQuery ? (
                        <>
                          Search: '
                          <span style={{ fontWeight: 600 }}>
                            {searchTmpQuery}
                          </span>
                          '
                        </>
                      ) : (
                        "Nearby Branches"
                      )}
                    </Typography>

                    {searchTmpQuery && (
                      <Button
                        onClick={handleClear}
                        variant="text"
                        color="primary"
                        sx={{
                          fontSize: "12px",
                          padding: 0,
                          minWidth: "auto",
                          textTransform: "none",
                          color: (theme) => theme.palette.primary.main,
                        }}
                      >
                        Clear
                      </Button>
                    )}
                  </Box>
                </Grid>
                <Grid item>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" sx={{ fontSize: "13px" }}>
                      Sort By:
                    </Typography>
                    <TextField
                      select
                      variant="standard"
                      size="small"
                      value={sortBy}
                      className="sortBySelect"
                      onChange={handleSortChange}
                      SelectProps={{ native: true }}
                    >
                      <option value={3}>Distance Ascending</option>
                      <option value={4}>Distance Descending</option>
                      <option value={1}>Name Ascending</option>
                      <option value={2}>Name Descending</option>
                    </TextField>
                  </Box>
                </Grid>
              </Grid>

              {/* Branch List */}
              <Box sx={{ maxHeight: 350, overflowY: "auto", pr: 1 }}>
                {isBranchesLoading && otherWarehouses.length === 0 ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={1}
                    marginTop={1}
                  >
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Skeleton key={i} variant="rounded" height={70} />
                    ))}
                  </Box>
                ) : otherWarehouses.length === 0 ? (
                  <Typography
                    variant="body2"
                    sx={{ mt: 2, color: "text.secondary" }}
                  >
                    No branches found.
                  </Typography>
                ) : (
                  otherWarehouses
                    .slice(0, visibleCount)
                    .map((branch) => (
                      <BranchCard
                        key={branch.branch.warehouseCode}
                        nearbyBranch={branch}
                        onSelect={(code) => setSelectedWarehouseCode(code)}
                      />
                    ))
                )}
              </Box>

              {/* Show More Button */}
              {visibleCount < otherWarehouses.length && (
                <Box textAlign="center" mt={2}>
                  <Button
                    variant="text"
                    color="primary"
                    onClick={() => setVisibleCount((prev) => prev + 3)}
                  >
                    Show More
                  </Button>
                </Box>
              )}
            </Grid>

            {/* RIGHT SIDE: Search + Map */}
            <Grid item xs={12} md={5}>
              <div className="pdp_searchContainer">
                <TextField
                  size="small"
                  variant="standard"
                  type="text"
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search Branches"
                  value={searchTmpQuery}
                  className="mapSearchInput"
                  onChange={(e) => setSearchTmpQuery(e.target.value)}
                  fullWidth
                  autoComplete="off"
                />
                <div style={{ display: "flex", gap: "5px" }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleMapSearch}
                  >
                    SEARCH
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleClear}
                  >
                    CLEAR
                  </Button>
                </div>
              </div>

              <Button
                variant="text"
                color="primary"
                startIcon={<MyLocationIcon color="primary" />}
                onClick={handleUseCurrentLocation}
                style={{
                  margin: "10px 0px",
                  fontSize: "12px",
                  textTransform: "none",
                }}
              >
                Use my current location
              </Button>

              <MapComponent
                mapKey="BRANCH_MAP_WAREHOUSE_DIALOG"
                selectedWarehouseCode={warehouseCode}
                warehouses={(currentWarehouse
                  ? [currentWarehouse, ...otherWarehouses]
                  : otherWarehouses
                ).map((x) => x?.branch)}
                longitudeCenterOffset={0}
                onMapLocationSelected={handleMapLocationSelected}
                disableControls={false}
                searchQuery={searchMapQuery}
                mapType={mapType}
              />
            </Grid>
          </Grid>

          {/* FOOTER */}
          <Typography
            variant="body2"
            align="center"
            style={{ color: "#af272f", marginTop: "30px", fontSize: "12px" }}
          >
            <i>
              Please Note: If you change your branch at this time, items in your
              cart may no longer be in stock and prices may change as these can
              vary by location. <br />
              If you wish to continue, we recommend saving items to a{" "}
              <Button
                variant="text"
                onClick={handleTemplateClick}
                sx={{
                  fontSize: "12px",
                  padding: 0,
                  minWidth: "auto",
                  textTransform: "none",
                  fontStyle: "italic",
                  color: "#54afe2",
                }}
              >
                Template
              </Button>{" "}
              for use at a later time.
            </i>
          </Typography>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BranchAvailabilityDialog;
